/**
 * Client Form Utilities
 *
 * Data transformation and utilities for the unified ClientForm component.
 * Handles both snake_case (database) and camelCase (enriched data) formats.
 */

import type { ClientRow, ClientType, ClientStatus, PaymentTerms } from '@/types/clients'
import type { ClientFormInput } from './client-form-schema'
import { clientFormDefaults } from './client-form-schema'

/**
 * camelCase format used by enriched data (hangar-view)
 */
export interface ClientFormInitialData {
  clientType: ClientType
  firstName?: string | null
  lastName?: string | null
  companyName?: string | null
  legalEntityName?: string | null
  emailPrimary?: string | null
  phonePrimary?: string | null
  phoneSecondary?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  city?: string | null
  stateProvince?: string | null
  postalCode?: string | null
  country?: string | null
  paymentTerms?: string | null
  taxExempt?: boolean
  taxId?: string | null
  status: ClientStatus
  notes?: string | null
}

/**
 * Convert ClientRow (snake_case database format) to form defaults
 */
export function clientRowToFormDefaults(client: ClientRow): ClientFormInput {
  return {
    client_type: client.client_type,
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    company_name: client.company_name || '',
    legal_entity_name: client.legal_entity_name || '',
    email_primary: client.email_primary || '',
    phone_primary: client.phone_primary || '',
    phone_secondary: client.phone_secondary || '',
    address_line1: client.address_line1 || '',
    address_line2: client.address_line2 || '',
    city: client.city || '',
    state_province: client.state_province || '',
    postal_code: client.postal_code || '',
    country: client.country || '',
    payment_terms: (client.payment_terms as PaymentTerms) || 'net_30',
    tax_exempt: client.tax_exempt || false,
    tax_id: client.tax_id || '',
    status: client.status,
    notes: client.notes || '',
  }
}

/**
 * Convert camelCase initialData to form defaults
 */
export function initialDataToFormDefaults(data: ClientFormInitialData): ClientFormInput {
  return {
    client_type: data.clientType,
    first_name: data.firstName || '',
    last_name: data.lastName || '',
    company_name: data.companyName || '',
    legal_entity_name: data.legalEntityName || '',
    email_primary: data.emailPrimary || '',
    phone_primary: data.phonePrimary || '',
    phone_secondary: data.phoneSecondary || '',
    address_line1: data.addressLine1 || '',
    address_line2: data.addressLine2 || '',
    city: data.city || '',
    state_province: data.stateProvince || '',
    postal_code: data.postalCode || '',
    country: data.country || '',
    payment_terms: (data.paymentTerms as PaymentTerms) || 'net_30',
    tax_exempt: data.taxExempt || false,
    tax_id: data.taxId || '',
    status: data.status,
    notes: data.notes || '',
  }
}

/**
 * Normalize either format to form defaults
 */
export function normalizeClientData(
  client?: ClientRow | null,
  initialData?: ClientFormInitialData | null
): ClientFormInput {
  if (client) {
    return clientRowToFormDefaults(client)
  }
  if (initialData) {
    return initialDataToFormDefaults(initialData)
  }
  return clientFormDefaults
}

/**
 * Field name mapping for prefocusField
 * Maps camelCase field names to snake_case form field names
 */
export const PREFOCUS_FIELD_MAP: Record<string, keyof ClientFormInput> = {
  firstName: 'first_name',
  lastName: 'last_name',
  companyName: 'company_name',
  legalEntityName: 'legal_entity_name',
  email: 'email_primary',
  emailPrimary: 'email_primary',
  phone: 'phone_primary',
  phonePrimary: 'phone_primary',
  phoneSecondary: 'phone_secondary',
  address: 'address_line1',
  addressLine1: 'address_line1',
  city: 'city',
  state: 'state_province',
  stateProvince: 'state_province',
  postalCode: 'postal_code',
  country: 'country',
  paymentTerms: 'payment_terms',
  taxId: 'tax_id',
  status: 'status',
  notes: 'notes',
  // Also support snake_case directly
  first_name: 'first_name',
  last_name: 'last_name',
  company_name: 'company_name',
  email_primary: 'email_primary',
  phone_primary: 'phone_primary',
}
